import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { de as deLocale } from 'date-fns/locale';
import { format } from 'date-fns';
import {
  AuditStatus,
  AuditType,
  MitarbeiterList,
  ProjektList,
} from '../../types';
import { AuditFormData, PendingZeitraum } from './auditDetailTypes';
import PendingZeitraeumeEditor from './PendingZeitraeumeEditor';
import { FieldLabel, PROJEKTKENNUNG_TOOLTIP } from './auditDetailShared';
import { projektnummerFehler } from './projektnummer';

interface AuditCreateFormProps {
  formData: AuditFormData;
  isNew: boolean;
  preselectedProjektId: number;
  auditStatuses: AuditStatus[];
  projekte: ProjektList[];
  auditTypes: AuditType[];
  mitarbeiter: MitarbeiterList[];
  pendingZeitraeume: PendingZeitraum[];
  saving: boolean;
  projektnummerAttempted: boolean;
  onInputChange: (field: keyof AuditFormData, value: string | number | number[] | undefined) => void;
  onAddPendingZeitraum: () => void;
  onUpdatePendingZeitraum: (
    tempId: number,
    field: keyof PendingZeitraum,
    value: string | number[],
  ) => void;
  onRemovePendingZeitraum: (tempId: number) => void;
  getPendingZeitraumDateError: (zeitraum: { startdatum: string; enddatum: string }) => string;
  onCancel: () => void;
  onSave: () => void;
}

const AuditCreateForm: React.FC<AuditCreateFormProps> = ({
  formData,
  isNew,
  preselectedProjektId,
  auditStatuses,
  projekte,
  auditTypes,
  mitarbeiter,
  pendingZeitraeume,
  saving,
  projektnummerAttempted,
  onInputChange,
  onAddPendingZeitraum,
  onUpdatePendingZeitraum,
  onRemovePendingZeitraum,
  getPendingZeitraumDateError,
  onCancel,
  onSave,
}) => {
  const projektnummerMeldung = projektnummerFehler(
    formData.projektnummer || '',
    projektnummerAttempted,
  );

  return (
  <Grid container spacing={3}>
    <Grid item xs={12}>
      <FieldLabel
        label="Name"
        tooltip='Eindeutiger Name des Audits, z.B. "Pentest Webapp Q1 2026"'
        required
      />
      <TextField
        fullWidth
        value={formData.name}
        onChange={(event) => onInputChange('name', event.target.value)}
        placeholder="z.B. Pentest Webapp Q1 2026"
      />
    </Grid>

    <Grid item xs={12}>
      <FieldLabel
        label="Beschreibung"
        tooltip="Interne Beschreibung des Audit-Umfangs und der Ziele. Wird nicht im Report angezeigt."
      />
      <TextField
        fullWidth
        value={formData.beschreibung}
        onChange={(event) => onInputChange('beschreibung', event.target.value)}
        multiline
        rows={4}
        placeholder="Umfang, Ziele und besondere Hinweise zum Audit…"
      />
    </Grid>

    <Grid item xs={12} sm={6}>
      <FieldLabel
        label="Status"
        tooltip="Aktueller Workflow-Status des Audits (z.B. Geplant, In Durchführung, Abgeschlossen). Steuert die Fortschrittsanzeige."
      />
      <FormControl fullWidth>
        <Select
          value={formData.audit_status_id || ''}
          onChange={(event) => onInputChange('audit_status_id', event.target.value as number)}
          displayEmpty
        >
          <MenuItem value="">
            <em>Kein Status</em>
          </MenuItem>
          {auditStatuses.map((status) => (
            <MenuItem key={status.id} value={status.id}>
              {status.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>

    <Grid item xs={12} sm={6}>
      <FieldLabel
        label="Projekt"
        tooltip="Das Kundenprojekt, dem dieses Audit zugeordnet ist. Bestimmt Kunde, Vertrag und Abrechnung."
        required
      />
      <FormControl fullWidth>
        <Select
          value={formData.projekt_id || ''}
          onChange={(event) => onInputChange('projekt_id', event.target.value as number)}
          displayEmpty
          disabled={isNew && preselectedProjektId > 0}
        >
          <MenuItem value="">
            <em>Keine Auswahl</em>
          </MenuItem>
          {projekte.map((projekt) => (
            <MenuItem key={projekt.id} value={projekt.id}>
              {projekt.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>

    <Grid item xs={12} sm={6}>
      <FieldLabel
        label="Projektnummer"
        tooltip="Interne Projektnummer. Festes Präfix P-, danach nur Ziffern, z. B. P-0042. Erscheint nur hier und auf der Audit-Detailseite."
        required
      />
      <TextField
        id="audit-projektnummer"
        fullWidth
        required
        value={formData.projektnummer || ''}
        onChange={(event) => onInputChange('projektnummer', event.target.value)}
        placeholder="P-0042"
        error={Boolean(projektnummerMeldung)}
        helperText={projektnummerMeldung || 'Festes Präfix P-, danach nur Ziffern, z. B. P-0042.'}
        inputProps={{ maxLength: 32, autoComplete: 'off', spellCheck: false }}
      />
    </Grid>

    <Grid item xs={12} sm={6}>
      <FieldLabel
        label="Projektkennung (Finding-Header)"
        tooltip={PROJEKTKENNUNG_TOOLTIP}
      />
      <TextField
        fullWidth
        value={formData.projektkennung || ''}
        onChange={(event) => onInputChange('projektkennung', event.target.value)}
        placeholder="z.B. ENBW"
      />
    </Grid>

    <Grid item xs={12} sm={6}>
      <FieldLabel
        label="Audit-Typen"
        tooltip="Art des Audits (z.B. Pentest, Code Review, Red Teaming). Mehrfachauswahl möglich. Bestimmt die farbigen Kürzel in Listen."
      />
      <FormControl fullWidth>
        <Select
          multiple
          value={formData.audit_type_ids || []}
          onChange={(event) =>
            onInputChange('audit_type_ids', event.target.value as number[])
          }
          input={<OutlinedInput />}
          displayEmpty
          renderValue={(selected) => {
            const values = selected as number[];
            if (values.length === 0) return <em style={{ opacity: 0.5 }}>Keine Auswahl</em>;

            return values
              .map((id) => {
                const type = auditTypes.find((entry) => entry.id === id);
                return type?.kuerzel || type?.name || '';
              })
              .join(', ');
          }}
        >
          {auditTypes.map((type) => (
            <MenuItem key={type.id} value={type.id}>
              <Checkbox checked={(formData.audit_type_ids || []).includes(type.id)} />
              <ListItemText primary={type.name} />
              {type.kuerzel && (
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    px: 0.75,
                    py: 0.125,
                    borderRadius: 0.75,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    color: '#fff',
                    backgroundColor: type.farbe || '#757575',
                  }}
                >
                  {type.kuerzel}
                </Box>
              )}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>

    <Grid item xs={12} sm={6}>
      <FieldLabel
        label="Preflight-Check Datum"
        tooltip="Termin für den Vorbereitungscall mit dem Kunden. Idealerweise 7–10 Tage vor Audit-Start, um Scope, Zugänge und offene Fragen zu klären."
      />
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={deLocale}>
        <DatePicker
          value={formData.preflight_datum ? new Date(formData.preflight_datum) : null}
          onChange={(date) =>
            onInputChange(
              'preflight_datum',
              date ? format(date, 'yyyy-MM-dd') : undefined,
            )
          }
          format="dd.MM.yyyy"
          slotProps={{
            textField: {
              fullWidth: true,
              placeholder: 'Datum wählen',
            },
          }}
        />
      </LocalizationProvider>
    </Grid>

    <Grid item xs={12} sm={6}>
      <FieldLabel
        label="Sprache"
        tooltip="Sprache des Audit-Reports (Deutsch oder Englisch). Beeinflusst die Reportgenerierung und Vorlagen."
        required
      />
      <ToggleButtonGroup
        value={formData.sprache || 'de'}
        exclusive
        onChange={(_, value) => {
          if (value) onInputChange('sprache', value);
        }}
        fullWidth
        sx={{ height: 56 }}
      >
        <ToggleButton value="de" sx={{ flex: 1, fontWeight: 600, fontSize: '0.9rem' }}>
          DE – Deutsch
        </ToggleButton>
        <ToggleButton value="en" sx={{ flex: 1, fontWeight: 600, fontSize: '0.9rem' }}>
          EN – English
        </ToggleButton>
      </ToggleButtonGroup>
    </Grid>

    <Grid item xs={12}>
      <FieldLabel
        label="Pentester"
        tooltip="Das Pentester-Team für dieses Audit. Dient der Ressourcenplanung und erscheint in Übersichten. Nur Mitarbeiter mit der Rolle „Pentester“ können zugewiesen werden."
      />
      <FormControl fullWidth>
        <Select
          multiple
          value={formData.mitarbeiter_ids || []}
          onChange={(event) => onInputChange('mitarbeiter_ids', event.target.value as number[])}
          input={<OutlinedInput />}
          displayEmpty
          renderValue={(selected) => {
            const values = selected as number[];
            if (values.length === 0) return <em style={{ opacity: 0.5 }}>Keine Pentester zugewiesen</em>;

            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {values.map((value) => {
                  const person = mitarbeiter.find((entry) => entry.id === value);
                  return (
                    <Chip
                      key={value}
                      label={person ? `${person.vorname} ${person.nachname}` : value}
                      size="small"
                    />
                  );
                })}
              </Box>
            );
          }}
        >
          {mitarbeiter.map((person) => (
            <MenuItem key={person.id} value={person.id}>
              <Checkbox checked={(formData.mitarbeiter_ids || []).indexOf(person.id) > -1} />
              <ListItemText primary={`${person.vorname} ${person.nachname}`} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>

    {isNew && (
      <Grid item xs={12}>
        <FieldLabel
          label="Durchführungszeiträume"
          tooltip="Konkrete Zeitfenster, in denen das Audit aktiv durchgeführt wird. Erscheinen im Auditkalender der jeweiligen Pentester zur Terminplanung und Auslastungsübersicht. Mehrere Phasen sind möglich (z.B. Phase 1: Recon, Phase 2: Exploitation)."
        />
        <PendingZeitraeumeEditor
          pendingZeitraeume={pendingZeitraeume}
          mitarbeiter={mitarbeiter}
          onAdd={onAddPendingZeitraum}
          onUpdate={onUpdatePendingZeitraum}
          onRemove={onRemovePendingZeitraum}
          getDateError={getPendingZeitraumDateError}
        />
      </Grid>
    )}

    <Grid item xs={12}>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        {!isNew && (
          <Button variant="outlined" onClick={onCancel}>
            Abbrechen
          </Button>
        )}
        <Button
          variant="contained"
          onClick={onSave}
          disabled={saving || !formData.name || !formData.projekt_id}
        >
          {saving ? <CircularProgress size={24} /> : 'Speichern'}
        </Button>
      </Box>
    </Grid>
  </Grid>
  );
};

export default AuditCreateForm;
